import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, ImagePlus, Film, Play, Image as ImageIcon } from "lucide-react";
import { resizeImageToDataURL, driveThumbnailUrl, parseDriveFileId, makeMediaId } from "@/lib/imageUtil";
import { toast } from "sonner";
import MediaViewer from "@/components/MediaViewer";

export default function MediaGallery({ media, onChange }) {
  const fileRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoCaption, setVideoCaption] = useState("");
  const [viewerStart, setViewerStart] = useState(null);

  const pickImage = () => fileRef.current?.click();

  const onFilesChosen = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const newItems = await Promise.all(
        files.map(async (f) => {
          const data = await resizeImageToDataURL(f, { maxEdge: 800, quality: 0.82 });
          return { id: makeMediaId(), type: "image", source: "upload", data };
        })
      );
      onChange([...(media || []), ...newItems]);
      toast.success(`Added ${newItems.length} image${newItems.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error("Couldn't process image");
    } finally {
      e.target.value = "";
    }
  };

  const addVideo = () => {
    const fid = parseDriveFileId(videoUrl);
    if (!fid) {
      toast.error("That doesn't look like a Google Drive share link.");
      return;
    }
    onChange([
      ...(media || []),
      {
        id: makeMediaId(),
        type: "video",
        source: "drive",
        url: videoUrl.trim(),
        caption: videoCaption.trim() || null,
      },
    ]);
    setVideoUrl("");
    setVideoCaption("");
    toast.success("Video added");
  };

  const remove = (id) => onChange((media || []).filter((m) => m.id !== id));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          onClick={pickImage}
          className="btn-ghost h-10 px-3"
          data-testid="media-add-image"
        >
          <ImagePlus className="h-4 w-4 mr-1.5" /> Add image
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={onFilesChosen}
          data-testid="media-image-input"
        />
      </div>

      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2">
        <div className="flex items-center gap-2 text-white/60 font-mono text-[11px] uppercase tracking-widest">
          <Film className="h-3.5 w-3.5 text-[#00e5ff]" /> Google Drive video
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://drive.google.com/file/d/…/view?usp=sharing"
            className="bg-transparent border-white/20 neon-input h-10 font-mono text-xs flex-1"
            data-testid="media-video-url"
          />
          <Input
            value={videoCaption}
            onChange={(e) => setVideoCaption(e.target.value)}
            placeholder="Caption (optional)"
            className="bg-transparent border-white/20 neon-input h-10 font-mono text-xs sm:w-40"
            data-testid="media-video-caption"
          />
          <Button
            type="button"
            onClick={addVideo}
            disabled={!videoUrl}
            className="btn-neon h-10 px-3"
            data-testid="media-add-video"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add
          </Button>
        </div>
        <p className="text-[10px] font-mono text-white/40">
          Set the file&apos;s share to <span className="text-white">&quot;Anyone with the
          link&quot;</span> in Drive, then paste it here.
        </p>
      </div>

      {media && media.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="media-grid">
          {media.map((m, i) => (
            <div
              key={m.id}
              className="group relative aspect-square rounded-lg overflow-hidden border border-white/10 bg-black"
              data-testid={`media-thumb-${m.id}`}
            >
              <button
                type="button"
                onClick={() => setViewerStart(i)}
                className="absolute inset-0"
              >
                {m.type === "image" ? (
                  <img
                    src={m.data || m.url}
                    alt=""
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                ) : (
                  <div className="w-full h-full bg-[#0a0a0a] flex items-center justify-center relative">
                    <img
                      src={driveThumbnailUrl(m.url) || ""}
                      alt=""
                      className="w-full h-full object-cover opacity-70"
                      draggable={false}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <Play className="h-7 w-7 text-white drop-shadow-md" fill="white" />
                    </div>
                  </div>
                )}
              </button>
              <button
                type="button"
                onClick={() => remove(m.id)}
                className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/70 text-white/80 hover:text-[#ff5a5a] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove"
                data-testid={`media-remove-${m.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
              <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-[9px] font-mono uppercase tracking-widest text-white/70 flex items-center gap-1">
                {m.type === "image" ? (
                  <ImageIcon className="h-2.5 w-2.5" />
                ) : (
                  <Film className="h-2.5 w-2.5" />
                )}
                {m.type}
              </div>
            </div>
          ))}
        </div>
      )}

      {viewerStart != null && (
        <MediaViewer items={media} startIndex={viewerStart} onClose={() => setViewerStart(null)} />
      )}
    </div>
  );
}
